import { z } from "zod";
import type { PromptSurface } from "../enums/prompts";
import { PROMPT_SURFACE } from "../enums/prompts";
import {
  type SocialAnalysePromptDocument,
  SocialAnalysePromptDocumentSchema,
} from "./social-analyse-document";
import {
  type YeetAgentPromptDocument,
  YeetAgentPromptDocumentSchema,
} from "./yeet-agent-document";
import {
  type YeetCanonicalizerPromptDocument,
  YeetCanonicalizerPromptDocumentSchema,
} from "./yeet-canonicalizer-document";

/**
 * The stored document shape per prompt surface. Adding a surface adds a member
 * here; `PromptDocument` widens to the union automatically, which breaks every
 * raw `row.document` read until it is narrowed by surface — so the DB column
 * never claims one surface's shape for all rows.
 */
export type PromptDocumentBySurface = {
  "yeet-agent": YeetAgentPromptDocument;
  "yeet-canonicalizer": YeetCanonicalizerPromptDocument;
  "agentic-social-analyse": SocialAnalysePromptDocument;
};

export type PromptDocument = PromptDocumentBySurface[PromptSurface];

// Both member schemas are .strict() with disjoint required keys, so the union
// parse is unambiguous; surface-match is enforced where the surface is known
// (parsePromptDocument).
export const PromptDocumentSchema = z.union([
  YeetAgentPromptDocumentSchema,
  YeetCanonicalizerPromptDocumentSchema,
  SocialAnalysePromptDocumentSchema,
]);

/**
 * Validate a stored document against its row's surface and narrow it to the
 * concrete shape. The exhaustive switch fails to compile when a surface is added
 * without a parser, so a new surface can never silently read as another's shape.
 * Zod-parsing also fails loudly on a corrupted jsonb blob rather than trusting it.
 */
export function parsePromptDocument<S extends PromptSurface>(
  surface: S,
  document: unknown,
): PromptDocumentBySurface[S] {
  const known: PromptSurface = surface;
  switch (known) {
    case PROMPT_SURFACE.YEET_AGENT:
      return YeetAgentPromptDocumentSchema.parse(
        document,
      ) as PromptDocumentBySurface[S];
    case PROMPT_SURFACE.YEET_CANONICALIZER:
      return YeetCanonicalizerPromptDocumentSchema.parse(
        document,
      ) as PromptDocumentBySurface[S];
    case PROMPT_SURFACE.SOCIAL_ANALYSE:
      return SocialAnalysePromptDocumentSchema.parse(
        document,
      ) as PromptDocumentBySurface[S];
    default:
      return assertNever(known);
  }
}

function assertNever(value: never): never {
  throw new Error(`Unhandled prompt surface: ${String(value)}`);
}
